import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { 
  LayoutDashboard, Package, User, MapPin, Heart, LogOut, 
  ShoppingBag, Bell, HelpCircle, Settings, ChevronRight
} from 'lucide-react';

const CustomerLayout = () => {
    const { user, isAuthenticated, logout, loading } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const [animationKey, setAnimationKey] = useState(location.pathname);

    useEffect(() => {
        if (!loading && !isAuthenticated) {
            navigate('/account?redirect=dashboard');
        }
    }, [isAuthenticated, loading, navigate]);

    useEffect(() => {
        setAnimationKey(location.pathname);
    }, [location.pathname]);

    if (loading) {
        return (
            <div className="customer-loading">
                <div className="loading-spinner-custom"></div>
            </div>
        );
    }

    if (!isAuthenticated) return null;

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const menuItems = [
        { path: '/account/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/account/orders', icon: ShoppingBag, label: 'My Orders' },
        { path: '/account/profile', icon: User, label: 'Profile Settings' },
        { path: '/account/address', icon: MapPin, label: 'Saved Addresses' },

    ];

    return (
        <div className="customer-layout-simple">
            <div className="layout-container">
                {/* Sidebar */}
                <aside className="sidebar-simple">
                    {/* User Card */}
                    <div className="user-card-simple">
                        <div className="user-avatar">
                            <span className="avatar-initial">
                                {user?.username?.charAt(0).toUpperCase() || 'U'}
                            </span>
                        </div>
                        <div className="user-info">
                            <h3 className="user-name">{user?.username || 'Customer'}</h3>
                            <p className="user-email">{user?.email || 'customer@example.com'}</p>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="nav-menu-simple">
                        {menuItems.map((item) => (
                            <NavLink 
                                key={item.path} 
                                to={item.path} 
                                end={item.path === '/account/dashboard'}
                                className={({ isActive }) => `nav-item-simple ${isActive ? 'active' : ''}`}
                            >
                                <item.icon size={18} />
                                <span>{item.label}</span>
                                <ChevronRight size={14} className="nav-arrow" />
                            </NavLink>
                        ))}
                    </nav>

                    {/* Logout Button */}
                    <button onClick={handleLogout} className="logout-btn-simple">
                        <LogOut size={18} />
                        <span>Sign Out</span>
                    </button>
                </aside>

                {/* Main Content */}
                <main className="main-content-simple">
                    <div key={animationKey} className="content-fade">
                        <Outlet />
                    </div>
                </main>
            </div>

            <style>{`
                .customer-loading {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f5f7fa;
                }

                .loading-spinner-custom {
                    width: 40px;
                    height: 40px;
                    border: 3px solid #e2e8f0;
                    border-top-color: #10b981;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                .customer-layout-simple {
                    min-height: 100vh;
                    background: #f5f7fa;
                }

                .layout-container {
                    max-width: 1280px;
                    margin: 0 auto;
                    padding: 24px;
                    display: flex;
                    gap: 24px;
                }

                /* Sidebar */
                .sidebar-simple {
                    width: 280px;
                    flex-shrink: 0;
                    position: sticky;
                    top: 24px;
                    height: fit-content;
                }

                /* User Card */
                .user-card-simple {
                    background: white;
                    border-radius: 16px;
                    padding: 20px;
                    margin-bottom: 16px;
                    text-align: center;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    border: 1px solid #e8ecef;
                }

                .user-avatar {
                    width: 64px;
                    height: 64px;
                    margin: 0 auto 12px;
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .avatar-initial {
                    font-size: 24px;
                    font-weight: 600;
                    color: white;
                }

                .user-name {
                    font-size: 16px;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 4px;
                }

                .user-email {
                    font-size: 12px;
                    color: #64748b;
                    margin: 0;
                }

                /* Navigation */
                .nav-menu-simple {
                    background: white;
                    border-radius: 16px;
                    padding: 8px 0;
                    margin-bottom: 16px;
                    border: 1px solid #e8ecef;
                }

                .nav-item-simple {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px 20px;
                    text-decoration: none;
                    color: #475569;
                    font-size: 14px;
                    font-weight: 500;
                    transition: all 0.2s ease;
                    position: relative;
                }

                .nav-item-simple:hover {
                    background: #f8fafc;
                    color: #10b981;
                }

                .nav-item-simple.active {
                    background: #f0fdf4;
                    color: #10b981;
                    border-left: 3px solid #10b981;
                }

                .nav-arrow {
                    margin-left: auto;
                    opacity: 0;
                    transition: all 0.2s ease;
                }

                .nav-item-simple:hover .nav-arrow {
                    opacity: 1;
                    transform: translateX(2px);
                }

                /* Logout Button */
                .logout-btn-simple {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px 20px;
                    background: white;
                    border: 1px solid #fee2e2;
                    border-radius: 12px;
                    color: #ef4444;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .logout-btn-simple:hover {
                    background: #fee2e2;
                }

                /* Main Content */
                .main-content-simple {
                    flex: 1;
                    min-width: 0;
                }

                .content-fade {
                    animation: fadeIn 0.3s ease;
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(5px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .layout-container {
                        flex-direction: column;
                        padding: 16px;
                    }

                    .sidebar-simple {
                        width: 100%;
                        position: static;
                    }
                }
            `}</style>
        </div>
    );
};

export default CustomerLayout;